import { Stack, useDisclosure } from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { InsuranceParamsPropertyDto, Property } from 'BrokerTsLib/src/dtos'
import { InitialGeneralParamsPropertyValues, InitialLiabilityValues, InitialPolicyHolderValues, InitialPropertyValues } from 'BrokerTsLib/src/dtos'
import type { Partner } from 'BrokerTsLib/src/enums'
import { Product, RiskVariant } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { GeneralParamsValidationSchema, PolicyHolderLiabilityValidationSchema, PolicyHolderPropertyValidationSchema, PropertyValidationRuleObject } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { ContractReplacementModal } from '@/components'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import { useInsurers } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useSaveInsuranceParams, useUpdateLastWizardStep } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { Parameters } from './containers'
import { Household } from './containers/Household'
import { Information } from './containers/Information'
import { Insuree } from './containers/Insuree'
import { Location } from './containers/Location'
import { Realty } from './containers/Realty'

export const schema = yup.object().shape({
  generalParams: GeneralParamsValidationSchema,
  policyHolder: PolicyHolderPropertyValidationSchema,
  property: yup.object().shape({
    ...PropertyValidationRuleObject,
    insurers: yup.array().nullable().optional(),
    range: yup.number().nullable().optional()
  }) as any,
})

export const schemaLiability = yup.object().shape({
  policyHolder: PolicyHolderLiabilityValidationSchema,
})

const getSchema = (product: Product | null): yup.ObjectSchema<any, yup.AnyObject, any, ''> => {
  return product == Product.LiabilityInsurance ? schemaLiability : schema
}

interface Step1FormProps {
  data?: InsuranceParamsPropertyDto
}

const getDefaultValue = (product: Product | null, partner?: keyof typeof Partner): InsuranceParamsPropertyDto => ({
  nextPage: 'next',
  generalParams: InitialGeneralParamsPropertyValues,
  policyHolder: InitialPolicyHolderValues,
  property: (product == Product.LiabilityInsurance ? InitialLiabilityValues(partner) : InitialPropertyValues(partner)) as Property,
})

export const Step1Sections = {
  Parameters: 'parameters',
  PolicyHolder: 'policyHolder',
  Location: 'location',
  Household: 'household',
  Realty: 'realty',
  AdditionalInfo: 'additionalInfo',
  Information: 'information',
}
export type Step1Sections = typeof Step1Sections[keyof typeof Step1Sections]

export const Step1 = ({ data }: Step1FormProps) => {
  const { product } = useApp()
  const [isLoading, setIsLoading] = useState(false)
  const [validationSchema, setValidationSchema] = useState(getSchema(product!))
  const navigate = useNavigate()
  const { isInsurerAvailable, data: insurersData } = useInsurers(false, null)
  const { currentPartner } = usePartner()

  const defVals = useMemo(() => {
    return {
      ...getDefaultValue(product, currentPartner?.partner ?? undefined),
    } as InsuranceParamsPropertyDto

  }, [product, currentPartner?.partner])

  const {
    triggerNewInsurance,
    clearTriggerNewInsurance,
    insuranceParamsId,
    refetchInsuranceParams,
    insuranceParams,
    contractReplacement,
    clearContractReplacement
  } = useInsuranceParams()
  const validationSummaryRef = useRef<HTMLDivElement>(null)
  const { mutateAsync: saveInsuranceParams } = useSaveInsuranceParams()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()

  const { isOpen: isSavedFormAlertOpen, onOpen: savedFormAlertOnOpen, onClose: savedFormAlertOnClose } = useDisclosure()
  const { isOpen: isOpenContractReplacementModal, onClose: onCloseContractReplacementModal, onOpen: onOpenContractReplacementModal } = useDisclosure()

  const methods = useForm<InsuranceParamsPropertyDto>({
    defaultValues: data,
    resolver: yupResolver(validationSchema) as any,
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    reset,
    watch
  } = methods

  const selectedType = watch('property.type')

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance)
      reset(defVals)

    clearTriggerNewInsurance()
  }, [triggerNewInsurance, reset])

  const [isMounted, setIsMounted] = useState(false)
  useLayoutEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted) return

    setValidationSchema(getSchema(product))
    setValue('property.insurers', insurersData?.data.map(x => x.id) ?? [])
    setValue('baseParams.insurers', insurersData?.data.map(x => x.id) ?? [])

    if (product == Product.PropertyInsurance) {
      defVals.property.type = selectedType
    }

    // pokud by to delalo problemy, tak zvazit, zda neresetovat vzdy a ne jen pokud se jedna o novou nabidku
    if (!data?.generalParams.paramsId)
      reset(defVals)
  }, [product, reset, isMounted, insurersData])

  useEffect(() => {
    if (contractReplacement) {
      onOpenContractReplacementModal()
      clearContractReplacement()
    }
  }, [contractReplacement])

  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.PropertyInsurance, 1, insuranceParamsId, methods)

  const submitForm = useCallback(handleSubmit((data) => {
    setIsLoading(true)
    const values = cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    if (values.policyHolder.insuredPerson) {
      values.policyHolder.insuredPerson.birthday = formatDate(values.policyHolder.insuredPerson.birthday)
    }
    if (values.property.data.concurrentInsuranceWithOtherInsurer) {
      values.property.data.concurrentHouseholdInsuranceValidTo = formatDate(values.property.data.concurrentHouseholdInsuranceValidTo)
      values.property.data.concurrentRealtyInsuranceValidTo = formatDate(values.property.data.concurrentRealtyInsuranceValidTo)
    }

    return saveInsuranceParams(values)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        let promiseChain: Promise<any> = Promise.resolve()
        if ((insuranceParams?.generalParams.lastWizardStep ?? 1) == 1) {
          promiseChain = promiseChain.then(() => { return updateStep({ paramsId: Number(response?.data), step: 2 }) })
        }

        if (insuranceParamsId) {
          promiseChain = promiseChain.then(() => { return refetchInsuranceParams() })
        }

        promiseChain.then(() => {
          if (values.nextPage == 'next') {
            navigate(`/property/steps/2/${response?.data}`)
          } else {
            savedFormAlertOnOpen()
          }

          setIsLoading(false)
        })

      })
      .catch((err) => {
        setIsLoading(false)

        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
      .finally(() => { })
  }), [handleSubmit])


  const { pageSections, setPageSections } = useSidebar()
  useEffect(() => {
    let i = 1
    const array: Section[] = [
      {
        id: Step1Sections.Parameters,
        sectionTitle: 'Parametry pojištění',
        index: i,
        children: (
          <Parameters
            number={i++}
            sectionTitle="Parametry pojištění"
          />
        )
      }
    ]

    if (product == Product.PropertyInsurance) {
      array.push({
        id: Step1Sections.Location,
        sectionTitle: 'Místo pojištění',
        index: i,
        children: (
          <Location
            number={i++}
            sectionTitle="Místo pojištění"
          />
        )
      })
    }

    if (selectedType && selectedType != RiskVariant.Household) {
      array.push({
        id: Step1Sections.Realty,
        sectionTitle: 'Nemovitost',
        index: i,
        children: (
          <Realty
            number={i++}
            sectionTitle="Nemovitost"
          />
        )
      })
    }

    if (selectedType && selectedType != RiskVariant.Realty) {
      array.push({
        id: Step1Sections.Household,
        sectionTitle: 'Domácnost',
        index: i,
        children: (
          <Household
            number={i++}
            sectionTitle="Domácnost"
          />
        )
      })
    }

    array.push({
      id: Step1Sections.PolicyHolder,
      sectionTitle: 'Pojistník',
      index: i,
      children: (
        <Insuree
          number={i++}
          sectionTitle="Pojistník"
        />
      )
    })

    array.push({
      id: Step1Sections.Information,
      sectionTitle: 'Obecné informace',
      index: i++,
      children: (
        <Information
          onContinue={() => {
            setValue('nextPage', 'next')
            return submitForm()
          }}
          onSave={() => {
            setValue('nextPage', 'current')
            return submitForm()
          }}
          isLoading={isLoading}
        />
      )
    })

    setPageSections(array)
  }, [selectedType, isInsurerAvailable])

  return (
    <FormProvider {...methods}>
      <form onSubmit={() => { }}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          <ErrorSummary errors={errors} />

          {!isOpenContractReplacementModal && <UnsavedFormModal {...insuranceParamsFormPersistance} />}

          <SubmitDialog
            isOpen={isSavedFormAlertOpen}
            onClose={savedFormAlertOnClose}
            header='Uloženo'
            body={!insuranceParamsId
              ? 'Nabídka byla uložena do rozpracovaných kalkulací'
              : 'Nabídka byla úspěšně uložena'
            }
          />

          <ContractReplacementModal
            isOpen={isOpenContractReplacementModal}
            onClose={() => onCloseContractReplacementModal()}
            modalContentProps={{ sx: { w: '100%', maxW: '800px' } }}
            title='Náhrada smlouvy'
          />
        </Stack>
      </form>
    </FormProvider>
  )
}
